/* ChopChop USA — cc-product buy box (agent B, 01/10/2026). Port of the approved mockup's [data-pdp] scripts on top of
 * Back Hero's live bh-product.js (bh → cc).
 * Offer mode: offer cards → unit pickers (assets/cc.js moves them into the chosen card, fix-44), gift tiles (scratch reveal,
 * "All N unlocked" in the heading, fix-46), nudge, save box, button + sticky bar (fix-61: deal + price + pack thumb).
 * Default mode: one variant select / option rows, optional quantity, "Free with every order" gifts.
 * Card prices = Shopify maths: each unit's % discount rounded DOWN to the cent (Back Hero live cart 30/09:
 * 2 × $99.95 at 5 % = $189.92), so card, button, sticky bar and checkout agree.
 * Add to cart = ONE /cart/add.js with every paid unit (per-unit variants) + the earned $0 gifts as child lines
 * (parent_id = the first paid variant of the same request — shopify-bundle-flow nested-gifts.md), then Horizon's drawer is
 * refreshed with the standard CartLinesUpdateEvent (sections in the answer) and opens.
 * Gifts sent = what the cart will earn minus what it already holds (window.CC.giftGuard.giftsForAdd: combo scaling, owner
 * 30/09 — 4 boards = Buy 3 set + Buy 1 set), so a second add never doubles nor misses gifts.
 * Gallery: assets/cc.js switches the main image on a thumb tap; this file adds the arrows, the "3 / 14" counter and swipe
 * (fix-57 look on the product media). Ship dates, lazy video, image viewer and back-to-top live in assets/cc.js. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const routes = () => window.Theme?.routes || { cart_url: '/cart', cart_add_url: '/cart/add' };
const jsonUrl = (u) => (u.endsWith('.js') ? u : `${u}.js`);

function moneyFmt(format) {
  return (cents) => {
    const f = format || '${{amount}}';
    const amt = (Math.round(cents) / 100).toFixed(2);
    const comma = amt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return f
      .replace(/\{\{\s*amount\s*\}\}/, comma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString('en-US'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amt.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toLocaleString('de-DE'))
      .replace(/<[^>]+>/g, '')
      .replace(/&amp;/g, '&');
  };
}

/** a unit's discount in cents: fixed amount (off, cents) or % rounded DOWN to the cent (Shopify) */
const unitOff = (cents, t) => (t.off > 0 ? Math.min(t.off, cents) : Math.floor((cents * (Number(t.pct) || 0)) / 100 + 1e-6));

/* ---------- gallery: arrows, counter, swipe (main image switching = assets/cc.js thumb click) ---------- */
function initGallery(g) {
  if (!g || g.dataset.ccGal) return;
  g.dataset.ccGal = '1';
  const row = $('.cc-gallery__thumbs', g);
  const count = $('[data-gcount]', g);
  if (!row) return;
  const items = () => $$(':scope > img, :scope > .cc-vthumb', row);
  const cur = () => Math.max(0, items().findIndex((x) => x.classList.contains('is-on') || x.querySelector?.('img.is-on')));
  const sync = () => {
    const list = items();
    const i = cur();
    if (count) count.textContent = `${i + 1} / ${list.length}`;
    const t = list[i];
    if (t && row.scrollWidth > row.clientWidth + 2) row.scrollTo({ left: t.offsetLeft - row.clientWidth / 2 + t.offsetWidth / 2, behavior: reduce() ? 'auto' : 'smooth' });
  };
  const go = (d) => {
    const list = items();
    if (list.length < 2) return;
    const t = list[(cur() + d + list.length) % list.length];
    (t.tagName === 'IMG' ? t : $('img', t) || t).click(); // assets/cc.js swaps the main image / plays the video
    sync();
  };
  g.addEventListener('click', (e) => {
    const a = e.target.closest('[data-gmove]');
    if (a) {
      go(Number(a.dataset.gmove));
      return;
    }
    if (e.target.closest('.cc-gallery__thumbs')) setTimeout(sync, 0);
  });
  const main = $('.cc-gallery__main', g);
  let x0 = null;
  let y0 = 0;
  main?.addEventListener('touchstart', (e) => {
    x0 = e.touches[0].clientX;
    y0 = e.touches[0].clientY;
  }, { passive: true });
  main?.addEventListener('touchend', (e) => {
    if (x0 === null) return;
    const dx = e.changedTouches[0].clientX - x0;
    const dy = e.changedTouches[0].clientY - y0;
    x0 = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
  }, { passive: true });
  main?.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') go(e.key === 'ArrowRight' ? 1 : -1);
  });
}

function init(pdp) {
  if (pdp.dataset.ccReady) return;
  pdp.dataset.ccReady = '1';
  const wrap = pdp.closest('.cc-product-sec');
  let cfg;
  try {
    cfg = JSON.parse($('script[data-cc-config]', wrap).textContent);
  } catch {
    return;
  }
  initGallery($('[data-cc-gallery]', pdp));
  const sid = cfg.section;
  const fmt = moneyFmt(cfg.money);
  const V = cfg.variants || [];
  const G = cfg.gifts || [];
  const byId = new Map(V.map((v) => [v.id, v]));
  const defV = byId.get(cfg.variant) || V.find((v) => v.a) || V[0];
  const bar = document.querySelector(`[data-cc-pbar="${sid}"]`);
  const sticky = bar && $('[data-cc-sticky]', bar);
  const stickyDeal = sticky && $('[data-sticky-deal]', sticky);
  const stickyPrice = sticky && $('[data-sticky-price]', sticky);
  const stickyImg = sticky && $('[data-sticky-img]', sticky);
  const stickyPhoto = stickyImg ? stickyImg.innerHTML : '';
  const stickyBtn = sticky && $('[data-sticky-atc]', sticky);
  const atc = $('[data-atc]', pdp);
  const atcPrice = $('[data-atc-price]', pdp);
  const atcText = $('[data-atc-text]', pdp);
  const err = $('[data-atc-err]', pdp);
  const offer = pdp.dataset.mode === 'offer';
  const compareOf = (v) => (v.c > v.p ? v.c : v.p);
  const findVariant = (opts) => V.find((v) => v.o.every((x, i) => opts[i] === undefined || x === opts[i]));

  /* ---------- option pickers ---------- */
  const readOpts = (scope) => {
    const out = [];
    for (const el of $$('[data-opt]', scope)) {
      const i = Number(el.dataset.opt);
      if (el.tagName === 'SELECT') out[i] = el.value;
      else if (el.classList.contains('is-on')) out[i] = el.dataset.val;
    }
    return out;
  };
  const setOpt = (scope, i, val) => {
    for (const el of $$(`[data-opt="${i}"]`, scope)) {
      if (el.tagName === 'SELECT') el.value = val;
      else {
        const on = el.dataset.val === val;
        el.classList.toggle('is-on', on);
        el.setAttribute('aria-checked', String(on));
      }
    }
    const nm = $(`[data-opt-name="${i}"]`, scope);
    if (nm) nm.textContent = val;
  };
  const showVariantImage = (v) => {
    if (!cfg.swatchImage || !v?.img) return;
    const main = $('[data-main]', pdp);
    if (!main) return;
    main.removeAttribute('srcset');
    main.src = v.img;
  };
  const optLabel = (scope) => $$('select[data-opt]', scope).map((s) => (s.options[s.selectedIndex]?.textContent || '').split(' — ')[0].trim()).join(' / ');

  let refresh = () => {};
  const onPick = (el) => {
    const unit = el.closest('.cc-unit[data-unit]');
    setOpt(unit || el.closest('.cc-units') || pdp, Number(el.dataset.opt), el.tagName === 'SELECT' ? el.value : el.dataset.val);
    showVariantImage(findVariant(readOpts(unit || pdp)));
    refresh();
  };
  pdp.addEventListener('click', (e) => {
    const sw = e.target.closest('.cc-swatch[data-opt]');
    if (sw) onPick(sw);
  });
  pdp.addEventListener('change', (e) => {
    const s = e.target.closest('select[data-opt]');
    if (s) onPick(s);
  });

  /* ---------- sticky bar (shows once the main button has scrolled above the screen) ---------- */
  const barCheck = () => {
    if (!bar || !atc) return;
    const on = !!sticky && atc.getBoundingClientRect().bottom < 0 && pdp.offsetParent !== null;
    if (sticky) sticky.classList.toggle('is-show', on);
    bar.classList.toggle('has-bar', on);
  };
  let raf = 0;
  document.addEventListener('scroll', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(barCheck);
  }, { passive: true, capture: true });
  window.addEventListener('resize', barCheck);
  const setSticky = (deal, price, pack) => {
    if (stickyDeal) stickyDeal.textContent = deal || '';
    if (stickyPrice) stickyPrice.textContent = price;
    if (!stickyImg) return;
    if (pack) {
      stickyImg.className = 'cc-sticky__img cc-sticky__img--pack';
      stickyImg.style.backgroundPosition = pack;
      stickyImg.replaceChildren();
    } else if (stickyImg.classList.contains('cc-sticky__img--pack')) {
      stickyImg.className = 'cc-sticky__img';
      stickyImg.style.backgroundPosition = '';
      stickyImg.innerHTML = stickyPhoto;
    }
  };

  /* ---------- busy / error ---------- */
  const setBusy = (on) => {
    for (const b of [atc, stickyBtn]) {
      if (!b) continue;
      if (on) b.setAttribute('aria-busy', 'true');
      else b.removeAttribute('aria-busy');
    }
  };
  const showErr = (msg) => {
    if (!err) return;
    err.textContent = msg || '';
    err.hidden = !msg;
  };

  /** ONE /cart/add.js, then the standard event so Horizon's drawer repaints from the sections and opens */
  async function send(items, from, meta) {
    const deferred = CartLinesUpdateEvent.createPromise();
    (from || atc).dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
    const ids = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const guard = window.CC?.giftGuard;
    // the drawer AND the whole cart come back in the add answer (sections/cc-product-cart): one request, no /cart.js read
    const want = guard ? [...ids, guard.cartSection] : ids;
    try {
      const res = await fetch(jsonUrl(routes().cart_add_url), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, ...(want.length ? { sections: want.join(','), sections_url: location.pathname } : {}) }),
      });
      const data = await res.json();
      let cart = !data.status && guard ? guard.cartFromAnswer(data) : null;
      if (!cart) cart = await (await fetch(jsonUrl(routes().cart_url), { headers: { Accept: 'application/json' } })).json();
      if (data.sections && guard) delete data.sections[guard.cartSection];
      guard?.noteCart(cart);
      if (data.status) {
        (from || atc).dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID', detail: { description: data.description } }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'cc-product' } });
        showErr(data.description || data.message || 'Could not add to cart.');
        return false;
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'cc-product', sourceId: sid, itemCount: items.reduce((s, i) => s + i.quantity, 0), productId: meta?.productId, sections: data.sections, didError: false },
      });
      // open the drawer (Horizon only opens it itself when "Auto-open cart drawer" is on); no drawer → cart page
      const cd = document.querySelector('cart-drawer-component');
      const td = cd?.closest('theme-drawer');
      if (!cd) location.href = routes().cart_url;
      else if (!cd.hasAttribute('auto-open') && td && !td.hasAttribute('open')) td.open?.();
      return true;
    } catch (e) {
      deferred.reject(e);
      showErr('Could not add to cart. Please try again.');
      return false;
    }
  }

  /** gifts to send with `units` paid units: the guard's combo maths minus what the cart holds; the page's own set without it */
  async function giftItems(units, tierNow, pickVariant) {
    const chosen = {};
    G.forEach((g, gi) => {
      if (g.handle && !chosen[g.handle]) chosen[g.handle] = pickVariant(gi);
    });
    const guard = window.CC?.giftGuard;
    if (guard) {
      try {
        return await guard.giftsForAdd(cfg.handle, units, (h) => chosen[h]);
      } catch {}
    }
    const out = [];
    G.forEach((g, gi) => {
      if (g.tier > tierNow || !g.handle) return;
      const id = pickVariant(gi);
      const hit = out.find((x) => x.id === id);
      const n = g.count * (g.perUnit ? units : 1);
      if (hit) hit.quantity += n;
      else if (id) out.push({ id, quantity: n });
    });
    return out;
  }
  // the variant the shopper picked on a gift tile (knife type, owner 02/10), else the gift's first variant
  const giftVariant = (gi) => Number($(`.cc-gb[data-gift="${gi}"]`, pdp)?.dataset.variant) || G[gi]?.variant || 0;

  /* gift variant toggle (knives: Japanese Kiritsuke / Stainless Steel): the hidden tile name and the struck value follow the
     choice; the chosen variant goes to the cart as that gift's child line (giftItems → guard.giftsForAdd pick). */
  pdp.addEventListener('click', (e) => {
    const b = e.target.closest('[data-gpick]');
    if (!b || b.disabled) return;
    e.stopPropagation();
    const tile = b.closest('.cc-gb');
    // gift-pick-photo (owner 22/10/2026): the photo in the gift box switches to the chosen version; a plain cut-out tile
    // (data-cut, gift_photo_prefix, owner 27/10/2026 utensils) switches to that knife's cut-out instead
    const pic = b.dataset.cut || b.dataset.img, picset = b.dataset.cut ? b.dataset.cutset : b.dataset.srcset;
    if (tile && pic) $$('.cc-gb__prod img', tile).forEach((im) => {
      if (picset) im.srcset = picset; else im.removeAttribute('srcset');
      im.src = pic;
      im.loading = 'eager';
    });
    if (!tile || tile.dataset.variant === b.dataset.gpick) return;
    tile.dataset.variant = b.dataset.gpick;
    $$('[data-gpick]', tile).forEach((x) => x.setAttribute('aria-checked', String(x === b)));
    const cap = $('.cc-gb__cap', tile);
    const nm = cap && $(':scope > b', cap);
    if (nm && b.dataset.name) nm.textContent = b.dataset.name;
    const s = cap && $('.cc-gb__p s', cap);
    if (s && b.dataset.value) s.textContent = b.dataset.value;
    // the photo stays the gift's featured image (both knives, owner 02/10); the cart line shows the chosen variant's image
    const g = G[Number(tile.dataset.gift)];
    if (g) g.pickTitle = b.dataset.name || '';
    refresh();
  }, true);

  let busy = false;
  let addNow = async () => {};
  const onAdd = async (from) => {
    if (busy) return;
    busy = true;
    setBusy(true);
    showErr('');
    try {
      await addNow(from);
    } finally {
      busy = false;
      setBusy(false);
    }
  };
  if (atc) atc.addEventListener('click', () => onAdd(atc));
  if (stickyBtn) stickyBtn.addEventListener('click', () => onAdd(stickyBtn));

  if (offer) offerMode();
  else defaultMode();
  barCheck();

  /* ================= offer mode ================= */
  function offerMode() {
    const T = cfg.tiers || [];
    const cards = $$('.cc-offer', pdp);
    const rows = $$('.cc-unit[data-unit]', pdp);
    const tiles = $$('.cc-gb', pdp);
    const nudge = $('[data-nudge]', pdp);
    const count = $('[data-gift-count]', pdp);
    const pips = $$('.cc-pips i', pdp);
    const sizeRows = $$('[data-gift-size]', pdp);
    // 27/10/2026 (owner, WakaPaw tunnel-bed style): buy_style = mix → one card per item with − / +, any mix. The deal is the biggest
    // tier the total reaches (2 = Buy 2, 3 = Buy 3, 4+ = Full Set); the add sends every unit, gifts by the guard's combo maths.
    const mix = $('[data-mix]', pdp);
    let mixList = [];
    const mixRead = () => {
      mixList = [];
      $$('[data-mix-card]', mix).forEach((c) => {
        const inp = $('input', c);
        const n = Math.max(0, Math.min(20, Math.round(Number(inp.value)) || 0));
        inp.value = String(n);
        c.classList.toggle('is-on', n > 0);
        const minus = $('[data-mx="-1"]', c);
        if (minus) minus.disabled = n <= 0;
        const v = byId.get(Number(c.dataset.variant));
        for (let i = 0; i < n && v; i++) mixList.push(v);
      });
      let k = 1;
      T.forEach((t, i) => {
        if (t.qty <= Math.max(1, mixList.length)) k = i + 1;
      });
      return k;
    };
    let cur = Number((cards.find((c) => c.classList.contains('is-on')) || cards[0])?.dataset.tier || 1);
    let ready = false;
    const tier = () => {
      const t = T[cur - 1] || { qty: 1, pct: 0, free: 0, name: '', sticky: '' };
      return mix ? { ...t, qty: mixList.length } : t;
    };

    const unitVariant = (i) => (mix ? mixList[i - 1] || defV : rows.length ? findVariant(readOpts(rows[i - 1] || rows[0])) || defV : defV);
    const isFree = (i, t) => {
      if (!t.free) return false;
      const step = Math.floor(t.qty / t.free);
      return step > 0 && i <= t.qty && i % step === 0;
    };
    const priceOf = (t) => {
      let sum = 0;
      let cmp = 0;
      let pay = 0;
      for (let i = 1; i <= t.qty; i++) {
        const v = unitVariant(i);
        sum += v.p;
        pay += v.p - unitOff(v.p, t);
        cmp += compareOf(v);
      }
      // free-unit tiers (Buy 1 Get 1) keep the plain maths: the free unit is exactly 100% off
      return { price: t.free ? Math.round((sum * (100 - t.pct)) / 100) : pay, compare: cmp };
    };

    function saveBox(t, price) {
      const box = $('[data-save]', pdp);
      if (!box) return;
      const list = $('[data-save-list]', box);
      const li = (name, val, free) => {
        const el = document.createElement('li');
        const a = document.createElement('span');
        a.textContent = name;
        const b = document.createElement('span');
        if (free) {
          b.className = 'free';
          const s = document.createElement('s');
          s.textContent = fmt(val);
          b.append(s, 'FREE');
        } else b.textContent = fmt(val);
        el.append(a, b);
        return el;
      };
      const out = [];
      let retail = 0;
      // one line per unit when units can differ (mockup: "Board 1 · S · 11″ × 7.9″"), else "2× Titanium Pan Pro"
      if (mix) {
        const by = new Map();
        mixList.forEach((v) => by.set(v, (by.get(v) || 0) + 1));
        by.forEach((n, v) => {
          const c = compareOf(v) * n;
          retail += c;
          out.push(li(`${n}× ${v.t}`, c, false));
        });
      } else if (rows.length && V.length > 1) {
        for (let i = 1; i <= t.qty; i++) {
          const c = compareOf(unitVariant(i));
          retail += c;
          out.push(li(`${cfg.unit} ${i} · ${optLabel(rows[i - 1] || rows[0])}${isFree(i, t) ? ' (FREE)' : ''}`, c, isFree(i, t)));
        }
      } else {
        const c = compareOf(unitVariant(1));
        retail += c * t.qty;
        out.push(li(`${t.qty}× ${cfg.short}`, c * t.qty, false));
      }
      G.forEach((g) => {
        if (g.tier > cur) return;
        const val = g.value * g.count * (g.perUnit ? t.qty : 1);
        retail += val;
        out.push(li(`Free gift: ${g.pickTitle || g.save || g.title}`, val, true));
      });
      list.replaceChildren(...out);
      $('[data-save-retail]', box).textContent = fmt(retail);
      $('[data-save-pay]', box).textContent = fmt(price);
      $('[data-save-amt]', box).textContent = fmt(retail - price);
      box.hidden = retail - price <= 0;
    }

    function gifts(animate) {
      let open = 0;
      let total = 0;
      tiles.forEach((el) => {
        const g = G[Number(el.dataset.gift)] || {};
        const n = Number(el.dataset.count) || 1;
        const on = Number(el.dataset.tier) <= cur;
        total += n;
        if (on) open += n;
        const was = el._want ?? el.classList.contains('is-open');
        el._want = on;
        if (el.getAttribute('role') === 'group') el.tabIndex = on ? -1 : 0; // pill tiles: focusable only while locked
        el.setAttribute('aria-label', on ? `${g.title || ''}, free with this offer` : `${g.title || ''}, locked. Choose ${T[Number(el.dataset.tier) - 1]?.name || 'a bigger offer'} to unlock`);
        if (on === was) return;
        clearTimeout(el._t);
        if (on && animate && !reduce()) {
          el.classList.add('is-shake');
          el._t = setTimeout(() => {
            el.classList.remove('is-shake');
            el.classList.add('is-open', 'is-pop');
            el._t = setTimeout(() => el.classList.remove('is-pop'), 800);
          }, 380);
        } else {
          el.classList.remove('is-shake', 'is-pop');
          el.classList.toggle('is-open', on);
        }
      });
      // all unlocked → said once in the gift heading, the nudge hides (fix-46: "gộp… cho nó gọn")
      const done = open >= total;
      if (count) {
        count.textContent = done ? `All ${total} unlocked` : `${open} of ${total} unlocked`;
        count.parentElement.classList.toggle('is-done', done);
      }
      pips.forEach((p, i) => p.classList.toggle('on', i < open));
      if (nudge) {
        nudge.classList.toggle('is-done', done);
        nudge.hidden = done;
        nudge.replaceChildren();
        if (!done) {
          const b = document.createElement('button');
          b.type = 'button';
          b.dataset.pickTop = '';
          b.textContent = nudge.dataset.label || T[T.length - 1]?.name || '';
          nudge.append('Pick ', b, ` to unbox all ${total} gifts`);
        }
      }
      sizeRows.forEach((r) => {
        const g = G[Number(r.dataset.giftSize)];
        r.hidden = !g || g.tier > cur;
      });
    }

    refresh = () => {
      const t = tier();
      const { price } = priceOf(t);
      rows.forEach((r) => {
        r.hidden = Number(r.dataset.unit) > t.qty;
      });
      // every card follows the chosen sizes (prices differ per variant)
      cards.forEach((c, k) => {
        const tt = T[k];
        if (!tt) return;
        const pc = priceOf(tt);
        c.dataset.price = fmt(pc.price);
        const p = $('[data-o-price]', c);
        if (p) p.textContent = fmt(pc.price);
        const s = $('[data-o-compare]', c);
        if (s) s.textContent = fmt(pc.compare);
        // pill after the name + (3-tier cards, 22/10/2026) the phone-only line under the price
        if (pc.compare > 0) $$('[data-o-save]', c).forEach((e) => { e.textContent = `Save ${Math.round(((pc.compare - pc.price) * 100) / pc.compare)}%`; });
        // hydrowata-style middle line (owner 22/10/2026): price per unit + You save follow the chosen sizes
        const ea = $('[data-o-each]', c);
        if (ea && tt.qty > 0) ea.textContent = fmt(pc.price / tt.qty);
        const ys = $('[data-o-yousave]', c);
        if (ys) {
          // You save = the same units bought one at a time − this offer's price (hydrowata rule)
          let single = 0;
          for (let i = 1; i <= tt.qty; i++) single += unitVariant(i).p;
          const d = single - pc.price;
          ys.textContent = fmt(Math.max(0, d));
          ys.parentElement.hidden = d <= 0;
        }
      });
      if (atcPrice) atcPrice.textContent = fmt(price);
      const card = cards[cur - 1];
      setSticky(t.sticky || `${t.name}${t.pill ? ` · ${t.pill}` : ''}`, fmt(price), card?.dataset.pack || '');
      const anyOut = Array.from({ length: t.qty }, (_, i) => unitVariant(i + 1)).some((v) => !v.a);
      if (atc) {
        atc.disabled = anyOut;
        if (atcText) atcText.textContent = anyOut ? 'Sold out' : cfg.atc;
      }
      if (stickyBtn) stickyBtn.disabled = anyOut;
      saveBox(t, price);
      if (mix) mixPaint(t, price);
      pdp.dataset.tier = String(cur);
    };

    function mixPaint(t, price) {
      const n = t.qty;
      const rungs = $$('[data-mix-step]', mix);
      const next = rungs.find((r) => Number(r.dataset.mixStep) > n);
      let reached = null;
      rungs.forEach((r) => {
        const q = Number(r.dataset.mixStep);
        r.classList.toggle('is-done', q <= n);
        r.classList.toggle('is-next', r === next);
        if (q <= n) reached = r;
      });
      const noun = n === 1 ? mix.dataset.one : mix.dataset.many;
      const what = $('[data-mix-what]', mix);
      const nx = $('[data-mix-next]', mix);
      if (what) {
        what.replaceChildren();
        const b = document.createElement('b');
        b.textContent = n ? `${n} ${noun}` : mix.dataset.empty;
        what.append(b);
      }
      const single = mixList.reduce((a, v) => a + v.p, 0);
      // owner 2/11/2026: no "You save $X" in the offers
      if (nx) nx.textContent = next ? `Add ${Number(next.dataset.mixStep) - n} more · ${next.dataset.deal}` : '';
      // nothing picked: the button says so, without "· $0.00" (the " · " is the text node before the price)
      const sep = atcPrice && atcPrice.previousSibling;
      if (sep && sep.nodeType === 3) sep.textContent = n ? ' · ' : '';
      if (atcPrice) atcPrice.hidden = !n;
      if (!n) {
        if (atc) atc.disabled = true;
        if (atcText) atcText.textContent = mix.dataset.empty;
        if (stickyBtn) stickyBtn.disabled = true;
      }
      setSticky(n ? `${n} ${noun}${reached ? ` · ${reached.dataset.deal}` : ''}` : mix.dataset.empty, fmt(price), '');
    }

    function select(n, animate = ready) {
      if (!T[n - 1]) return;
      const same = n === cur && ready;
      cur = n;
      if (!same) {
        cards.forEach((c) => {
          const on = Number(c.dataset.tier) === n;
          c.classList.toggle('is-on', on);
          c.setAttribute('aria-checked', String(on));
          c.tabIndex = on ? 0 : -1;
        });
      }
      gifts(animate);
      refresh();
    }


    cards.forEach((c) => {
      c.addEventListener('click', (e) => {
        // a tap on the size pickers that live inside the chosen card is not a card pick
        if (e.target.closest('.cc-units, select, a')) return;
        select(Number(c.dataset.tier));
        // the scroll after a pick lives in assets/offer-scroll.js (same on every store, owner 22/10/2026)
      });
      c.addEventListener('keydown', (e) => {
        if (e.target !== c) return;
        const k = e.key;
        if (k === 'Enter' || k === ' ') {
          e.preventDefault();
          select(Number(c.dataset.tier));
        } else if (k === 'ArrowDown' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowLeft') {
          e.preventDefault();
          const d = k === 'ArrowDown' || k === 'ArrowRight' ? 1 : -1;
          const n = ((Number(c.dataset.tier) - 1 + d + cards.length) % cards.length) + 1;
          select(n);
          cards[n - 1].focus();
        }
      });
    });

    // gift tiles: locked → pick the tier that unlocks it; open → nothing (owner 30/09: no info popup); nudge → top tier
    pdp.addEventListener('click', (e) => {
      if (e.target.closest('[data-pick-top]')) {
        select(Number(nudge?.dataset.top) || T.length);
        return;
      }
      const el = e.target.closest('.cc-gb');
      if (el && !el.classList.contains('is-open')) select(Number(el.dataset.tier));
    });
    // a gift tile with variant pills is a div (role=group): Enter / Space on a LOCKED one picks its tier, like the buttons
    pdp.addEventListener('keydown', (e) => {
      const el = e.target.closest?.('.cc-gb[role="group"]');
      if (!el || e.target !== el || el.classList.contains('is-open') || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      select(Number(el.dataset.tier));
    });

    // ?offer=N pre-selects offer N before anything animates
    const want = Number(new URLSearchParams(location.search).get('offer'));
    pdp.classList.add('gb-init');
    if (mix) {
      mix.addEventListener('click', (e) => {
        const b = e.target.closest('[data-mx]');
        if (!b || b.disabled) return;
        const inp = $('input', b.closest('[data-mix-card]'));
        inp.value = String(Math.max(0, (Number(inp.value) || 0) + Number(b.dataset.mx)));
        select(mixRead());
      });
      mix.addEventListener('change', (e) => {
        if (e.target.closest('[data-mix-card]')) select(mixRead());
      });
    }
    select(mix ? mixRead() : want >= 1 && want <= T.length ? want : cur, false);
    void pdp.offsetWidth;
    requestAnimationFrame(() => requestAnimationFrame(() => pdp.classList.remove('gb-init')));
    ready = true;

    addNow = async (from) => {
      const t = tier();
      const paid = [];
      for (let i = 1; i <= t.qty; i++) {
        const v = unitVariant(i);
        const hit = paid.find((x) => x.id === v.id);
        if (hit) hit.quantity++;
        else paid.push({ id: v.id, quantity: 1 });
      }
      if (!paid.length) return;
      const gifts = await giftItems(t.qty, cur, giftVariant);
      const parent = paid[0].id;
      const items = [...paid, ...gifts.map((g) => ({ id: g.id, quantity: g.quantity, parent_id: parent }))];
      if (gifts.length) window.CC?.giftGuard?.notePdpAdd();
      await send(items, from, { productId: cfg.productId });
    };
  }

  /* ================= default mode ================= */
  function defaultMode() {
    const input = $('[data-qty-input]', pdp);
    const minus = $('[data-qty-step="-1"]', pdp);
    const plus = $('[data-qty-step="1"]', pdp);
    const priceRow = $('[data-cc-price]', pdp);
    const max = cfg.maxQty || 10;
    const qty = () => (input ? Math.max(1, Math.min(max, Math.round(Number(input.value)) || 1)) : 1);
    const variant = () => (V.length > 1 ? findVariant(readOpts(pdp)) : defV) || defV;
    const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;');
    refresh = () => {
      const v = variant();
      const q = qty();
      if (input) input.value = q;
      if (minus) minus.disabled = q <= 1;
      if (plus) plus.disabled = q >= max;
      if (priceRow) {
        const save = v.c > v.p ? Math.round((1 - v.p / v.c) * 100) : 0;
        priceRow.innerHTML = `<strong>${esc(fmt(v.p))}</strong>${save ? `<s>${esc(fmt(v.c))}</s><span class="cc-save-tag">Save ${save}%</span>` : ''}`;
      }
      if (atcPrice) atcPrice.textContent = fmt(v.p * q);
      if (atc) {
        atc.disabled = !v.a;
        if (atcText) atcText.textContent = v.a ? cfg.atc : 'Sold out';
      }
      if (stickyBtn) stickyBtn.disabled = !v.a;
      const lab = V.length > 1 ? optLabel(pdp) || v.o.join(' / ') : 'Free 3–5 Day Shipping';
      setSticky(`${lab}${q > 1 ? ` · Qty ${q}` : ''}`, fmt(v.p * q), '');
    };
    pdp.addEventListener('click', (e) => {
      const b = e.target.closest('[data-qty-step]');
      if (!b || b.disabled || !input) return;
      input.value = qty() + Number(b.dataset.qtyStep);
      refresh();
    });
    input?.addEventListener('change', refresh);
    addNow = async (from) => {
      const v = variant();
      if (!v?.a) return;
      const q = qty();
      const gifts = G.length ? await giftItems(q, 1, giftVariant) : [];
      const items = [{ id: v.id, quantity: q }, ...gifts.map((g) => ({ id: g.id, quantity: g.quantity, parent_id: v.id }))];
      if (gifts.length) window.CC?.giftGuard?.notePdpAdd();
      await send(items, from, { productId: cfg.productId });
    };
    refresh();
  }
}

function initAll(root = document) {
  $$('[data-cc-pdp]', root).forEach(init);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
else initAll();
document.addEventListener('shopify:section:load', (e) => initAll(e.target));
